import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router";
import { useMediaQuery } from "react-responsive";
import { mediaQueryBreakpoints } from "melodies-source/Theme/mediaQueries";

export const MenuContext = createContext(undefined);
export const MenuConsumer = MenuContext.Consumer;

export type MenuMode = "hidden" | "collapsed" | "expanded";

interface ContextValue {
  menuMode: MenuMode;
  setMenuMode: Dispatch<SetStateAction<MenuMode>>;
  toggleMenuMode: () => void;
  isMobile: boolean;
}
export const MenuProvider = ({ children }) => {
  const [menuMode, setMenuMode] = useState<MenuMode>("expanded");
  const isMobile = !useMediaQuery({
    query: mediaQueryBreakpoints.desktop,
  });
  const isMenuCollapsed = useMediaQuery({
    query: mediaQueryBreakpoints.menuCollapsed,
  });

  const location = useLocation();

  const toggleMenuMode = () => {
    setMenuMode(
      menuMode === "expanded"
        ? isMobile
          ? "hidden"
          : "collapsed"
        : "expanded",
    );
  };

  useEffect(() => {
    setMenuMode(
      isMobile ? "hidden" : isMenuCollapsed ? "collapsed" : "expanded",
    );
  }, [isMobile, isMenuCollapsed]);

  useEffect(() => {
    if (isMobile || isMenuCollapsed) {
      setMenuMode(isMobile ? "hidden" : "collapsed");
    }
  }, [location.pathname]);

  const value: ContextValue = useMemo(
    () => ({ menuMode, setMenuMode, toggleMenuMode, isMobile }),
    [menuMode, isMobile, isMenuCollapsed],
  );

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>;
};
export const useMenuContext = (): ContextValue => useContext(MenuContext);
